import type { Project } from "@openreel/core";

export interface AutoSaveConfig {
  interval: number;
  maxSlots: number;
  enabled: boolean;
  debounceTime: number;
}

export interface AutoSaveMetadata {
  id: string;
  projectId: string;
  projectName: string;
  timestamp: number;
  slot: number;
  isRecovery: boolean;
  /** Opening frame of the project, for the dashboard's Recent cards. */
  thumbnail?: string;
}

/**
 * The poster of whatever plays first, or undefined when there is no footage.
 *
 * Only a data URL is taken: media posters are generated at import with
 * canvas.toDataURL, and an object URL would be dead by the next reload.
 */
function firstFrameOf(project: Project): string | undefined {
  const clips = project.timeline.tracks
    .flatMap((track) => track.clips)
    .filter((clip) => !/^(text|shape|svg|sticker)-/.test(clip.mediaId))
    .sort((a, b) => a.startTime - b.startTime);

  for (const clip of clips) {
    const url = project.mediaLibrary.items.find((m) => m.id === clip.mediaId)?.thumbnailUrl;
    if (url && url.startsWith("data:")) return url;
  }
  return undefined;
}

/**
 * The project as it is worth storing: everything except what can be rebuilt
 * from the media blobs, which are already persisted on their own in the media
 * store (see media-storage.ts).
 *
 * waveformData is the expensive one. It is a Float32Array of roughly
 * duration * 100 samples, and JSON.stringify writes a typed array key by key
 * ("0":0.13,"1":0.09,...), so a ten minute track cost around sixty thousand
 * entries on every single save. Filmstrips are up to ten thumbnails per item
 * and recovery discards them regardless (restoreMediaItem clears the field).
 * Both are regenerated in the background once a project is on screen.
 *
 * blob and fileHandle go too. The record is now stored as a structured clone,
 * and a clone would copy the entire source file into the save rather than
 * collapsing it to {} the way JSON did. A null blob is also what
 * restoreMediaItem's own guard expects, so a media item whose blob is missing
 * from the media store now falls through instead of reaching
 * URL.createObjectURL with a {}.
 *
 * A blob: thumbnail is dropped as well: object URLs are dead after a reload,
 * and recovery already regenerates a poster whenever thumbnailUrl is missing.
 */
function toPersistableProject(project: Project): Project {
  return {
    ...project,
    mediaLibrary: {
      ...project.mediaLibrary,
      items: project.mediaLibrary.items.map((item) => ({
        ...item,
        blob: null,
        fileHandle: null,
        waveformData: null,
        filmstripThumbnails: undefined,
        thumbnailUrl: item.thumbnailUrl?.startsWith("blob:")
          ? null
          : item.thumbnailUrl,
      })),
    },
  };
}

/** Whether a failed write was the structured clone algorithm refusing a value. */
function isCloneError(error: unknown): boolean {
  if (typeof DOMException !== "undefined" && error instanceof DOMException) {
    return error.name === "DataCloneError";
  }
  return error instanceof Error && error.name === "DataCloneError";
}

/**
 * What every listing needs: the fields the Recent cards, the project switcher
 * and slot cleanup read. Small, and the only thing an index iteration touches.
 */
interface AutoSaveHeader {
  id: string;
  projectId: string;
  projectName: string;
  timestamp: number;
  slot: number;
  thumbnail?: string;
  /**
   * The account this save belongs to on THIS device. Auto-saves are what the
   * dashboard's Recent list and the project switcher actually read, so leaving
   * them unowned is what let a fresh account see the previous account's
   * projects. Pre-ownership records carry undefined and are adopted by the
   * first account to sign in; see setOwner.
   */
  ownerId?: string;
}

/**
 * The project itself, in its own record beside the header under a derived key.
 *
 * Kept apart because the two are read at wildly different rates: the header of
 * every save is listed after every single write (cleanupOldSaves) and again for
 * every project on the device when the welcome screen draws its Recent cards,
 * while a body is read exactly once, when someone actually recovers. With both
 * in one record, `getAll` had to reconstruct the whole object graph of every
 * save just to compare six numbers, and for a project heavy in baked scene3d
 * meshes that is most of the cost of the save it was supposed to make cheap.
 *
 * A body deliberately carries NONE of the indexed fields. IndexedDB leaves a
 * record out of an index when it has no value at that index's key path, so
 * iterating the projectId index yields headers only, with no body deserialised.
 * That property is what makes the split work without a second object store, so
 * do not add projectId, timestamp or slot to this shape.
 */
interface AutoSaveBody {
  id: string;
  /**
   * Handed to IndexedDB as a structured clone. Building a JSON string of the
   * whole project on the main thread every two seconds is the pause users felt
   * after each edit; the clone skips the string entirely and keeps typed arrays
   * and Blobs as themselves.
   */
  snapshot?: Project;
  /**
   * JSON body. Written by every save before the structured-clone switch, and
   * still written as a fallback when something in the project refuses to
   * clone. A body carries one of `snapshot` or `data`, never both.
   */
  data?: string;
}

/**
 * A record as read back. Saves written before the header/body split carry both
 * halves in one object, so every read path has to tolerate the union.
 */
type AutoSaveRecord = AutoSaveHeader & Partial<AutoSaveBody>;

/**
 * Body records live in the same object store as their header, under the
 * header's id plus this suffix. Same store rather than a new one so the schema
 * stays at version 1: a version bump blocks on any other tab that still has the
 * database open, and there is nothing here worth that failure mode.
 */
const BODY_KEY_SUFFIX = "::body";

function bodyKeyFor(headerId: string): string {
  return `${headerId}${BODY_KEY_SUFFIX}`;
}

const DEFAULT_CONFIG: AutoSaveConfig = {
  interval: 30000, // 30 seconds
  maxSlots: 3,
  enabled: true,
  debounceTime: 2000, // 2 seconds
};

const AUTO_SAVE_DB_NAME = "openreel-autosave";
const AUTO_SAVE_DB_VERSION = 1;
const AUTO_SAVE_STORE = "autosaves";

type AutoSaveEventType = "saved" | "restored" | "error" | "recoveryAvailable";
type AutoSaveEventCallback = (data?: unknown) => void;

export class AutoSaveManager {
  private config: AutoSaveConfig;
  private db: IDBDatabase | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private debounceTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private lastSavedHash: string = "";
  private currentSlot: number = 0;
  private listeners: Map<AutoSaveEventType, Set<AutoSaveEventCallback>> =
    new Map();

  /**
   * What to save next: either a snapshot, or a thunk that produces one. The
   * store passes a thunk so that assembling the full project (which pulls text,
   * shape, SVG and sticker clips out of the engines) happens once per save
   * instead of once per edit.
   */
  private pendingProject: Project | (() => Project) | null = null;
  private isDirty: boolean = false;

  constructor(config: Partial<AutoSaveConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  async initialize(): Promise<void> {
    try {
      this.db = await this.openDatabase();
    } catch (error) {
      console.error("[AutoSave] Failed to initialize:", error);
      this.emit("error", { error, message: "Failed to initialize auto-save" });
    }
  }

  private openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (typeof indexedDB === "undefined") {
        reject(new Error("IndexedDB not supported"));
        return;
      }

      const request = indexedDB.open(AUTO_SAVE_DB_NAME, AUTO_SAVE_DB_VERSION);

      request.onerror = () => {
        reject(
          new Error(
            `Failed to open auto-save database: ${request.error?.message}`,
          ),
        );
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(AUTO_SAVE_STORE)) {
          const store = db.createObjectStore(AUTO_SAVE_STORE, {
            keyPath: "id",
          });
          store.createIndex("projectId", "projectId", { unique: false });
          store.createIndex("timestamp", "timestamp", { unique: false });
          store.createIndex("slot", "slot", { unique: false });
        }
      };
    });
  }

  start(getProject: () => Project): void {
    if (!this.config.enabled) {
      return;
    }

    this.stop(); // Stop any existing auto-save

    // Initial save
    this.pendingProject = getProject;
    this.saveIfDirty();

    // Set up periodic saves
    this.intervalId = setInterval(() => {
      this.pendingProject = getProject;
      this.saveIfDirty();
    }, this.config.interval);
  }

  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.debounceTimeoutId) {
      clearTimeout(this.debounceTimeoutId);
      this.debounceTimeoutId = null;
    }
  }

  /**
   * The project is required. Marking dirty without saying what changed used to
   * mean "save whatever was last stored", which under a thunk depends on
   * whichever of start(), the store subscription or forceSave() wrote it last:
   * an argument nobody can reason about at the call site.
   */
  markDirty(project: Project | (() => Project)): void {
    // Capture the state that caused this dirty notification. The debounce can
    // fire well before the periodic refresh in start(), so relying on the
    // previous pendingProject would save an older snapshot. A thunk is
    // resolved at save time, which keeps the per-edit cost to a timer reset.
    this.pendingProject = project;
    this.isDirty = true;

    // Debounce the save
    if (this.debounceTimeoutId) {
      clearTimeout(this.debounceTimeoutId);
    }

    this.debounceTimeoutId = setTimeout(() => {
      this.saveIfDirty();
    }, this.config.debounceTime);
  }

  private resolvePending(): Project | null {
    const pending = this.pendingProject;
    if (!pending) {
      return null;
    }
    return typeof pending === "function" ? pending() : pending;
  }

  private async saveIfDirty(): Promise<void> {
    if (!this.isDirty) {
      return;
    }

    const project = this.resolvePending();
    if (!project) {
      return;
    }

    const hash = this.computeHash(project);

    if (hash === this.lastSavedHash) {
      return; // No changes
    }

    try {
      await this.save(project);
      this.lastSavedHash = hash;
      this.isDirty = false;
    } catch (error) {
      console.error("[AutoSave] Save failed:", error);
      this.emit("error", { error, message: "Auto-save failed" });
    }
  }

  private async save(project: Project): Promise<void> {
    if (!this.db) {
      throw new Error("Auto-save database not initialized");
    }

    const header: AutoSaveHeader = {
      id: `${project.id}-slot-${this.currentSlot}`,
      projectId: project.id,
      projectName: project.name,
      timestamp: Date.now(),
      slot: this.currentSlot,
      // Stamped at write time, so every save belongs to whoever made it.
      ownerId: this.ownerId ?? undefined,
      // A frame from the project, so the Recent cards show the work instead of
      // one film glyph repeated. Stored beside the body rather than derived on
      // read, because reading would mean unpacking every save's whole project
      // just to draw a list. Taken from the live project, before stripping,
      // since the poster is one of the media items' own thumbnails.
      thumbnail: firstFrameOf(project),
    };

    const snapshot = toPersistableProject(project);
    const bodyId = bodyKeyFor(header.id);

    try {
      await this.saveRecords(header, { id: bodyId, snapshot });
    } catch (error) {
      if (!isCloneError(error)) {
        throw error;
      }
      // Something in the project is not structured-cloneable. JSON drops those
      // values silently rather than refusing the whole write, so it is what
      // stands between the user and a lost save.
      console.warn(
        "[AutoSave] Project is not structured-cloneable, storing JSON:",
        error,
      );
      await this.saveRecords(header, {
        id: bodyId,
        data: JSON.stringify(snapshot),
      });
    }

    this.currentSlot = (this.currentSlot + 1) % this.config.maxSlots;
    await this.cleanupOldSaves(project.id);

    this.emit("saved", {
      projectId: project.id,
      timestamp: header.timestamp,
      slot: header.slot,
    });
  }

  /**
   * Both halves of one save, in a single transaction so a header can never
   * survive a body that was never written.
   */
  private saveRecords(
    header: AutoSaveHeader,
    body: AutoSaveBody,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error("Database not initialized"));
        return;
      }

      const tx = this.db.transaction(AUTO_SAVE_STORE, "readwrite");
      try {
        const store = tx.objectStore(AUTO_SAVE_STORE);
        // Body first: put() runs the structured clone synchronously, so a
        // refusal throws here with the header not yet queued and the
        // transaction commits nothing. The caller then retries with JSON.
        store.put(body);
        store.put(header);
      } catch (error) {
        reject(error);
        return;
      }

      tx.oncomplete = () => resolve();
      tx.onerror = () =>
        reject(new Error(`Failed to save: ${tx.error?.message}`));
      tx.onabort = () =>
        reject(new Error(`Failed to save: ${tx.error?.message}`));
    });
  }

  private async cleanupOldSaves(currentProjectId: string): Promise<void> {
    if (!this.db) return;

    const allSaves = await this.getAllHeaders();
    const projectSaves = allSaves.filter(
      (s) => s.projectId === currentProjectId,
    );

    if (projectSaves.length > this.config.maxSlots) {
      const toDelete = projectSaves
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(this.config.maxSlots);

      for (const save of toDelete) {
        await this.deleteRecord(save.id);
      }
    }
  }

  /** Drops a save entirely: header and body are one save, never one without
   * the other. */
  private deleteRecord(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error("Database not initialized"));
        return;
      }

      const tx = this.db.transaction(AUTO_SAVE_STORE, "readwrite");
      const store = tx.objectStore(AUTO_SAVE_STORE);
      store.delete(id);
      store.delete(bodyKeyFor(id));

      tx.oncomplete = () => resolve();
      tx.onerror = () =>
        reject(new Error(`Failed to delete: ${tx.error?.message}`));
      tx.onabort = () =>
        reject(new Error(`Failed to delete: ${tx.error?.message}`));
    });
  }

  /**
   * Every save's header, and no project bodies.
   *
   * Read through the projectId index rather than store.getAll(): the index
   * holds only records that have a projectId, which is exactly the headers.
   * store.getAll() would structured-clone every stored project back out, and
   * this runs after every single save and once per project on the welcome
   * screen. Saves written before the header/body split still carry their body
   * inline and are still returned whole; there is a bounded number of them and
   * each is replaced by the next save to that slot.
   */
  private getAllHeaders(): Promise<AutoSaveRecord[]> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error("Database not initialized"));
        return;
      }

      const tx = this.db.transaction(AUTO_SAVE_STORE, "readonly");
      const store = tx.objectStore(AUTO_SAVE_STORE);
      const request = store.index("projectId").getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () =>
        reject(new Error(`Failed to get saves: ${request.error?.message}`));
    });
  }

  /** The signed-in account. Driven by the auth store; see auth-store.ts. */
  private ownerId: string | null = null;

  async setOwner(ownerId: string | null): Promise<void> {
    if (this.ownerId === ownerId) return;
    this.ownerId = ownerId;
    if (ownerId) await this.adoptUnownedSaves(ownerId);
  }

  /** First account in claims the pre-ownership saves, same as the project
   * store: hiding them for everyone would orphan work that exists nowhere
   * else, and deleting it would destroy it. */
  private async adoptUnownedSaves(ownerId: string): Promise<void> {
    if (!this.db) {
      try { await this.initialize(); } catch { return; }
    }
    if (!this.db) return;
    return new Promise((resolve) => {
      const tx = this.db!.transaction(AUTO_SAVE_STORE, "readwrite");
      const store = tx.objectStore(AUTO_SAVE_STORE);
      // Over the index, so the cursor walks headers and never rewrites a body.
      const request = store.index("projectId").openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return;
        const item = cursor.value as AutoSaveRecord;
        if (item.ownerId === undefined) cursor.update({ ...item, ownerId });
        cursor.continue();
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  }

  async checkForRecovery(projectId?: string): Promise<AutoSaveMetadata[]> {
    if (!this.db) {
      await this.initialize();
    }

    try {
      const allSaves = await this.getAllHeaders();

      // Only the signed-in account's saves. Signed out, none are listed.
      let saves = allSaves.filter(
        (s) => this.ownerId !== null && s.ownerId === this.ownerId,
      );
      if (projectId) {
        saves = saves.filter((s) => s.projectId === projectId);
      }

      const metadata: AutoSaveMetadata[] = saves
        .sort((a, b) => b.timestamp - a.timestamp)
        .map((s) => ({
          id: s.id,
          projectId: s.projectId,
          projectName: s.projectName,
          timestamp: s.timestamp,
          slot: s.slot,
          isRecovery: true,
          thumbnail: s.thumbnail,
        }));

      if (metadata.length > 0) {
        this.emit("recoveryAvailable", { saves: metadata });
      }

      return metadata;
    } catch (error) {
      console.error("[AutoSave] Failed to check for recovery:", error);
      return [];
    }
  }

  async recover(saveId: string): Promise<Project | null> {
    if (!this.db) {
      await this.initialize();
    }

    try {
      const record = await this.getRecord(saveId);
      if (!record) {
        console.warn(`[AutoSave] No save found with id: ${saveId}`);
        return null;
      }

      // Saves written before the header/body split hold the body inline; the
      // rest keep it in a sibling record, which is only fetched here.
      const body =
        record.snapshot || record.data
          ? record
          : await this.getRecord(bodyKeyFor(saveId));

      // Saves written before the structured-clone switch carry `data` instead.
      const project = body?.snapshot
        ? body.snapshot
        : body?.data
          ? (JSON.parse(body.data) as Project)
          : null;

      if (!project) {
        console.warn(`[AutoSave] Save ${saveId} has no project body`);
        return null;
      }

      this.emit("restored", { project, timestamp: record.timestamp });
      return project;
    } catch (error) {
      console.error("[AutoSave] Recovery failed:", error);
      this.emit("error", { error, message: "Failed to recover project" });
      return null;
    }
  }

  private getRecord(id: string): Promise<AutoSaveRecord | null> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error("Database not initialized"));
        return;
      }

      const tx = this.db.transaction(AUTO_SAVE_STORE, "readonly");
      const store = tx.objectStore(AUTO_SAVE_STORE);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () =>
        reject(new Error(`Failed to get record: ${request.error?.message}`));
    });
  }

  async getMostRecentSave(projectId: string): Promise<AutoSaveMetadata | null> {
    const saves = await this.checkForRecovery(projectId);
    return saves.length > 0 ? saves[0] : null;
  }

  async clearProjectSaves(projectId: string): Promise<void> {
    if (!this.db) return;

    const allSaves = await this.getAllHeaders();
    const projectSaves = allSaves.filter((s) => s.projectId === projectId);

    for (const save of projectSaves) {
      await this.deleteRecord(save.id);
    }
  }

  async clearAllSaves(): Promise<void> {
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(AUTO_SAVE_STORE, "readwrite");
      const store = tx.objectStore(AUTO_SAVE_STORE);
      const request = store.clear();

      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () =>
        reject(new Error(`Failed to clear: ${request.error?.message}`));
    });
  }

  private computeHash(project: Project): string {
    const motionCompositions = project.motionCompositions ?? [];
    const motionLayerCount = motionCompositions.reduce(
      (sum, composition) => sum + (composition.layers?.length ?? 0),
      0,
    );
    const motionAudioClipCount = motionCompositions.reduce(
      (sum, composition) => sum + (composition.audioClips?.length ?? 0),
      0,
    );
    const motionModifiedAt = motionCompositions.reduce(
      (max, composition) => Math.max(max, composition.modifiedAt ?? 0),
      0,
    );

    const key = JSON.stringify({
      id: project.id,
      modifiedAt: project.modifiedAt,
      trackCount: project.timeline.tracks.length,
      clipCount: project.timeline.tracks.reduce(
        (sum, t) => sum + t.clips.length,
        0,
      ),
      mediaCount: project.mediaLibrary.items.length,
      motionCompositionCount: motionCompositions.length,
      motionLayerCount,
      motionAudioClipCount,
      motionInstanceCount: project.motionInstances?.length ?? 0,
      motionModifiedAt,
    });

    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      const char = key.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  }

  updateConfig(config: Partial<AutoSaveConfig>): void {
    this.config = { ...this.config, ...config };
  }

  getConfig(): AutoSaveConfig {
    return { ...this.config };
  }

  on(event: AutoSaveEventType, callback: AutoSaveEventCallback): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  off(event: AutoSaveEventType, callback: AutoSaveEventCallback): void {
    this.listeners.get(event)?.delete(callback);
  }

  private emit(event: AutoSaveEventType, data?: unknown): void {
    this.listeners.get(event)?.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error("[AutoSave] Event callback error:", error);
      }
    });
  }

  async forceSave(project: Project): Promise<void> {
    this.pendingProject = project;
    this.isDirty = true;
    await this.saveIfDirty();
  }

  /**
   * Whether the given project has edits that have not been persisted to the
   * latest auto-save slot. Returns false for a pristine session (no edits yet)
   * and once the pending edits have been flushed. Used by the desktop
   * unsaved-changes guard on quit/close.
   */
  hasUnsavedChanges(project: Project): boolean {
    if (!this.isDirty) {
      return false;
    }
    return this.computeHash(project) !== this.lastSavedHash;
  }

  destroy(): void {
    this.stop();
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    this.listeners.clear();
  }
}

export const autoSaveManager = new AutoSaveManager();

export async function initializeAutoSave(): Promise<void> {
  await autoSaveManager.initialize();
}

export function startAutoSave(getProject: () => Project): void {
  autoSaveManager.start(getProject);
}

export function stopAutoSave(): void {
  autoSaveManager.stop();
}

export async function checkForRecovery(
  projectId?: string,
): Promise<AutoSaveMetadata[]> {
  return autoSaveManager.checkForRecovery(projectId);
}

export async function recoverProject(saveId: string): Promise<Project | null> {
  return autoSaveManager.recover(saveId);
}
